import {produce} from 'immer';
import {createStore} from 'zustand';
import {combine} from 'zustand/middleware';

import {
  DocumentArrayGroup,
  DocumentBaseGroup,
  DocumentField,
  DocumentModel,
  DocumentValue,
  MetaValue,
  ValueResolver,
} from '@/engine';
import {collectAllArrayFields, findMaxArrayIndex} from '@/engine/utils/array-fields';
import {SupportedLocale} from '@/initializers/i18next';
import {isFieldInputType} from '@/model/utils/fields';
import {
  BATCH_OPERATIONS,
  BatchUpdate,
  DocumentState,
  DocumentStateMethods,
  FieldError,
  ValidateSectionMode,
  Validity,
} from '@/types/document/store';
import {getAllFields} from '@/utils/document/group';
import {validateField as doValidateField, validateSection as doValidateSection} from '@/utils/document/validation';

export type DocumentStore = DocumentState & DocumentStateMethods;

export type DocumentStoreApi = ReturnType<typeof createDocumentStore>;

export type StateReducer = (state: DocumentState) => DocumentState;

const isDocumentArrayGroup = (group: DocumentArrayGroup | DocumentBaseGroup): group is DocumentArrayGroup => {
  return 'rows' in group;
};

const NON_DELETEABLE_FIELDS = ['A0'];

const clearSection = (sectionId: string, state: DocumentState, documentModel: DocumentModel): DocumentState => {
  return produce(state, draft => {
    const section = documentModel.sections.find(section => section.id === sectionId);
    const dependentSectionIds = section?.displayWith ?? [];
    const dependentSections = documentModel.sections.filter(section => dependentSectionIds.includes(section.id));

    if (!section) return;

    const groups = [...section.groups, ...dependentSections.flatMap(section => section.groups)];
    const fieldIds = groups.flatMap(group => {
      const fieldId = (field: DocumentField) => field.id;

      if (isDocumentArrayGroup(group)) {
        const count = Number(state.data[`_${group.id}[count]`]);
        let groupIndexPrivateFieldIds: string[] = [];

        // Find the actual maximum index in the data to handle overflow cases
        // Pass the group definition to also scan for template field patterns like J17#*, J18#*
        const maxIndex = findMaxArrayIndex(state.data, group.id, group);

        // Use the maximum between count and maxIndex+1 to ensure all elements are cleared
        const iterationLimit = Math.max(isNaN(count) ? 0 : count, maxIndex + 1);

        // Collect ALL field IDs for this array, including template fields like J17#*, J18#*
        const actualFieldIds = collectAllArrayFields(state.data, group, iterationLimit - 1);

        if (iterationLimit > 0) {
          for (let index = 0; index < iterationLimit; index++) {
            groupIndexPrivateFieldIds = groupIndexPrivateFieldIds.concat([
              `_${group.id}#${index}[draft]`,
              `_${group.id}#${index}[new]`,
              `_${group.id}#${index}[open]`,
              `_${group.id}#${index}[fields]`,
            ]);
          }
        }

        return [...actualFieldIds, ...groupIndexPrivateFieldIds, `_${group.id}[count]`];
      } else {
        return group.fields.map(fieldId);
      }
    });

    delete draft.validity[sectionId];

    fieldIds.forEach(fieldId => {
      if (NON_DELETEABLE_FIELDS.includes(fieldId)) return;

      delete draft.data[fieldId];
    });

    return draft;
  });
};

const clearForm = (state: DocumentState, documentModel: DocumentModel) => {
  return produce(state, draft => {
    return documentModel.sections.reduce((draft, section) => {
      return clearSection(section.id, draft, documentModel);
    }, draft);
  });
};

const updateField =
  (fieldId: string, value: DocumentValue | DocumentValue[]): StateReducer =>
  state => {
    return produce(state, draft => {
      draft.data[fieldId] = value;
    });
  };

const registerAttachment =
  (sectionId: string, fieldId: string, value: string): StateReducer =>
  state => {
    return produce(state, draft => {
      draft.attachments[sectionId] = draft.attachments[sectionId] ?? {};
      draft.attachments[sectionId].fields = draft.attachments[sectionId].fields ?? {};
      draft.attachments[sectionId].fields[fieldId] = value;
    });
  };

const unregisterAttachment =
  (sectionId: string, fieldId: string): StateReducer =>
  state => {
    return produce(state, draft => {
      delete draft.attachments[sectionId]?.fields[fieldId];
    });
  };

const unregisterAttachments = (): StateReducer => state => {
  return produce(state, draft => {
    draft.attachments = {};
  });
};

const unregisterSectionAttachments =
  (sectionId: string): StateReducer =>
  state => {
    return produce(state, draft => {
      delete draft.attachments[sectionId];
    });
  };

const updateMeta =
  (fieldId: keyof MetaValue, value: string): StateReducer =>
  state => {
    return produce(state, draft => {
      draft.meta[fieldId] = value;
    });
  };

const unsetField =
  (fieldId: string): StateReducer =>
  state => {
    const {data, ...rest} = state;
    const {[fieldId]: _, ...restData} = data;
    return {data: restData, ...rest};
  };

const touchField =
  (
    sectionId: string,
    fieldId: string,
    options: {
      documentModel: DocumentModel;
    },
  ): StateReducer =>
  state => {
    const {documentModel} = options;

    const section = documentModel.sections.find(section => section.id === sectionId);
    if (!section) return state;

    const field = section.groups.flatMap(getAllFields).find(field => field.id === fieldId);
    if (!field) return state;

    if (!field.visible || !isFieldInputType(field)) return state;

    return produce(state, draft => {
      draft.validity[sectionId] = draft.validity[sectionId] ?? {};
      draft.validity[sectionId].fields = draft.validity[sectionId].fields ?? {};
      draft.validity[sectionId].fields[fieldId] = {...(draft.validity[sectionId].fields[fieldId] ?? {}), touched: true};
    });
  };

const validateField =
  (
    sectionId: string,
    fieldId: string,
    options: {
      documentModel: DocumentModel;
      valueResolver: ValueResolver;
    },
  ): StateReducer =>
  state => {
    const {documentModel, valueResolver} = options;

    const section = documentModel.sections.find(section => section.id === sectionId);
    if (!section) return state;

    const field = section.groups.flatMap(getAllFields).find(field => field.id === fieldId);
    if (!field) return state;

    if (!field.visible || !isFieldInputType(field)) return state;

    return produce(state, draft => {
      draft.validity[sectionId] = draft.validity[sectionId] ?? {};
      draft.validity[sectionId].fields = draft.validity[sectionId].fields ?? {};
      draft.validity[sectionId].fields[fieldId] = {
        ...(draft.validity[sectionId].fields[fieldId] ?? {}),
        ...{touched: true},
        ...doValidateField(field, valueResolver),
      };
    });
  };

const validateSection =
  (
    sectionId: string,
    options: {
      documentModel: DocumentModel;
      valueResolver: ValueResolver;
      validateMode?: ValidateSectionMode;
      validateFields?: string[];
    },
  ): StateReducer =>
  state => {
    const {documentModel, valueResolver, validateFields = [], validateMode = ValidateSectionMode.All} = options;

    const section = documentModel.sections.find(section => section.id === sectionId);
    if (!section) return state;

    return produce(state, draft => {
      if (validateMode === ValidateSectionMode.All) {
        draft.validity[sectionId] = doValidateSection(section, valueResolver);
      } else {
        const sectionValidity = doValidateSection(section, valueResolver);

        // Keep fields current reference, as we don't want it to change
        const fields = draft.validity[sectionId]?.fields ?? {};

        let updatedFields: Record<string, Validity> = fields;

        // If we only validate touched fields, we modify the updatedFields object
        // to only include the modification from the sectionValidity. Untouched fields should be left as they are.
        if (validateMode == ValidateSectionMode.OnlyTouchedFields) {
          updatedFields = {};
          for (const field in sectionValidity.fields) {
            if (fields[field]?.touched) {
              updatedFields[field] = {...sectionValidity.fields[field], touched: true};
            } else if (fields[field] && !fields[field].valid) {
              updatedFields[field] = {...sectionValidity.fields[field], touched: fields[field].touched};
            } else if (!sectionValidity.fields[field].valid) {
              updatedFields[field] = {
                valid: true,
                touched: false,
                willBeValidOnceTouched: sectionValidity.fields[field].valid,
              };
            }
          }
        }

        if (validateMode == ValidateSectionMode.Fields) {
          updatedFields = {};
          for (const field in sectionValidity.fields) {
            if (validateFields.includes(field)) {
              updatedFields[field] = {...sectionValidity.fields[field], touched: true};
            } else if (fields[field] && !fields[field].valid) {
              updatedFields[field] = {...sectionValidity.fields[field], touched: fields[field].touched};
            } else if (!sectionValidity.fields[field].valid) {
              updatedFields[field] = {
                valid: true,
                touched: false,
                willBeValidOnceTouched: sectionValidity.fields[field].valid,
              };
            }
          }
        }

        draft.validity[sectionId] = sectionValidity;
        draft.validity[sectionId].fields = updatedFields;
      }
    });
  };

const applyBatchUpdate =
  (patch: BatchUpdate[]): StateReducer =>
  state => {
    return produce(state, draft => {
      for (const update of patch) {
        switch (update.type) {
          case BATCH_OPERATIONS.SET:
            draft.data[update.id] = update.value;
            break;
          case BATCH_OPERATIONS.INCREMENT:
            draft.data[update.id] = (Number(draft.data[update.id] ?? '0') + (update.value ?? 1)).toFixed();
            break;
          case BATCH_OPERATIONS.DECREMENT:
            draft.data[update.id] = (Number(draft.data[update.id] ?? '0') - (update.value ?? 1)).toFixed();
            break;
          case BATCH_OPERATIONS.UNSET:
            delete draft.data[update.id];
            break;
          case BATCH_OPERATIONS.UNSET_VALIDITY:
            delete draft.validity[update.sectionId].fields[update.fieldId];
            break;
          case BATCH_OPERATIONS.MOVE: {
            const value = draft.data[update.fromId];
            if (value === undefined) {
              delete draft.data[update.toId];
            } else {
              draft.data[update.toId] = value;
            }
            delete draft.data[update.fromId];
            break;
          }
        }
      }
    });
  };

const setSectionValidity =
  (sectionId: string, isValid: boolean, errors: FieldError[]): StateReducer =>
  state => {
    return produce(state, draft => {
      draft.validity[sectionId] = isValid
        ? {valid: true, fields: {}}
        : {
            valid: false,
            errors,
            fields: {},
          };
    });
  };

const getInitialState = (): DocumentState => ({
  data: {},
  attachments: {},
  meta: {id: '', name: '', type: '', updatedAt: null},
  validity: {},
  // Français is selected by default in the UI, the value needs to reflect that
  locale: 'fr' as SupportedLocale,
  navigation: {
    returnToPreviousModalId: undefined,
  },
});

export const createDocumentStore = (initialState?: Partial<DocumentState>) =>
  createStore(
    combine<DocumentState, DocumentStateMethods>({...getInitialState(), ...(initialState ?? {})}, (set, get) => ({
      changeLocale: locale => set({locale}),
      setReturnToPreviousModalId: returnToPreviousModalId =>
        set(state => ({navigation: {...state.navigation, returnToPreviousModalId}})),
      clearSection: (sectionId, documentModel) => set(state => clearSection(sectionId, state, documentModel)),
      clearForm: documentModel => set(state => clearForm(state, documentModel)),
      registerAttachment: (sectionId, fieldId, value) => set(registerAttachment(sectionId, fieldId, value)),
      unregisterAttachment: (sectionId, fieldId) => set(unregisterAttachment(sectionId, fieldId)),
      unregisterSectionAttachments: sectionId => set(unregisterSectionAttachments(sectionId)),
      unregisterAttachments: () => set(unregisterAttachments()),
      updateMeta: (fieldId, value) => set(updateMeta(fieldId, value)),
      updateField: (fieldId, value) => set(updateField(fieldId, value)),
      unsetField: fieldId => set(unsetField(fieldId)),
      batchUpdate: patch => set(applyBatchUpdate(patch)),
      validateSection: (sectionId, options) => {
        set(validateSection(sectionId, options));
        return get().validity[sectionId]?.valid ?? false;
      },
      setSectionValidity: (sectionId, isValid, errors = []) => set(setSectionValidity(sectionId, isValid, errors)),
      touchField: (sectionId, fieldId, options) => {
        set(touchField(sectionId, fieldId, options));
        return get().validity[sectionId]?.fields[fieldId]?.touched ?? false;
      },
      validateField: (sectionId, fieldId, options) => {
        set(validateField(sectionId, fieldId, options));
        return get().validity[sectionId]?.fields[fieldId]?.valid ?? false;
      },
    })),
  );
